import { Result } from '..';

export interface HTTPClient {
  get<T>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>>;
  delete<T>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>>;
  head<T>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>>;
  options<T>(url: string, config?: RequestConfig): Promise<HTTPResponse<T>>;
  post<T, R = unknown>(url: string, data?: R, config?: RequestConfig): Promise<HTTPResponse<T>>;
  put<T, R = unknown>(url: string, data?: R, config?: RequestConfig): Promise<HTTPResponse<T>>;
  patch<T, R = unknown>(url: string, data?: R, config?: RequestConfig): Promise<HTTPResponse<T>>;
}

export interface RequestConfig {
  headers?: Record<string, string>;
  baseURL?: string;
  params?: unknown;
  signal?: AbortSignal;
  timeout?: number;
}

export type HTTPMethod = keyof {
  [
    K in keyof HTTPClient as HTTPClient[K] extends (...args: any) => Promise<HTTPResponse<never>>
      ? K
      : never
  ]: never;
};

export interface HTTPRequestFactory<T = unknown> {
  method: HTTPMethod;
  url: string;
  headers: Record<string, string>;
  try(): Promise<HTTPResponse<T>>;
}

export type HTTPRequestCompletionCallback<T = unknown, R = unknown> = (
  result: Result<HTTPResponse<T>>,
  request: HTTPRequestFactory<R>,
) => void;

export type HTTPErrorHandler<E, T = unknown, R = unknown> = (
  response: HTTPResponse<T>,
  request: HTTPRequestFactory<R>,
  extraData: E,
) => void;

export interface HTTPResponse<T = unknown> {
  status: number;
  data: T;
}

export interface HTTPError<T = unknown> extends Error {
  response: HTTPResponse<T>;
}

export const HTTP_METHODS = ['get', 'delete', 'head', 'options', 'post', 'put', 'patch'] as const;

export const isHTTPError = <T = unknown>(e: unknown): e is HTTPError<T> => {
  return (
    e instanceof Error &&
    'response' in e &&
    typeof e.response === 'object' &&
    e.response !== null &&
    'status' in e.response &&
    typeof e.response.status === 'number'
  );
};

export const isHTTPErrorResponse = (
  response: HTTPResponse<unknown>,
): response is HTTPResponse<{ error: string }> =>
  response.status >= 400 &&
  typeof response.data === 'object' &&
  response.data !== null &&
  'error' in response.data &&
  typeof response.data.error === 'string';
